import React from 'react';
import styled from 'styled-components';
import { PropTypes } from 'prop-types';
import Constants from '../../submodules/logictry_config/constants';
import LogicBaseCollectionCache from '../../services/cache/LogicBaseCollectionCache';
import TreeCache from '../../services/cache/TreeCache';
import LogicBaseUserCache from '../../services/cache/LogicBaseUserCache';
import WindowSize from '../../services/WindowSize';
import UserAccount from '../../services/UserAccount';
import LogicBaseAppList from '../LogicBaseAppList';
import AppConstants from '../../submodules/logictry_config/s3_config.json';
import convertColorToHex from '../../utils/convertcolortohex';
import ColorPicker from '../ColorPicker';
import Popup from '../Popup';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DeleteIcon,
} from '../../styledhtml/Icon';
import { isColorInvalid, isUrlValid } from '../../utils/regex';
import ConfirmDialog from '../ConfirmDialog';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import ContentSourceField from '../ContentSourceField';
import {
  getCollectionAppsPage,
  getCollectionAppsPageMeta,
  nextCollectionAppsLoadedCount,
} from '../../utils/collectionAppsFetch';

// Tailwind-styled components
import {
  Card,
  ModalSheetTitle,
  Form,
  Input,
  InputBlock,
  Label,
  DialogActions,
  ConfirmButton,
  CancelButton,
  SecondaryTextButton,
  WarningText,
  CheckboxRow,
  SelectField,
  InlineFieldRow,
  TextArea,
} from '../../styledhtml/Tailwind';
import Tooltip from '../Tooltip';

// Styles specific to this page
const UserProfile = styled.div`
  margin: 0.5rem 0 0;
`;

const AppSort = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  > div {
    width: 16px;
    height: 16px;
  }
`;

const TagRow = styled.div`
  display: flex;
  // flex-wrap: wrap;
  gap: 0 2rem;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const EmptySortSpacer = styled.div`
  width: 1rem;
`;

export default class LogicBaseCollectionEditPopup extends LogictryComponent {
  static propTypes = {
    collection: PropTypes.object,
    onClose: PropTypes.func,
    manageContent: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      error: '',
      appTopics: JSON.parse(
        JSON.stringify((props.collection && props.collection.appTopics) || {})
      ),
      confirmDeletePopup: null,
      loadedPublicAppCount: undefined,
    };
  }

  componentDidMount() {
    LogicBaseCollectionCache.onStateUpdate(this);
    UserAccount.onStateUpdate(this);
    WindowSize.onStateUpdate(this);
    TreeCache.onStateUpdate(this);
    LogicBaseUserCache.onStateUpdate(this);
  }

  componentWillUnmount() {
    LogicBaseCollectionCache.offStateUpdate(this);
    UserAccount.offStateUpdate(this);
    WindowSize.offStateUpdate(this);
    TreeCache.offStateUpdate(this);
    LogicBaseUserCache.offStateUpdate(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.collection?._id !== this.props.collection?._id) {
      this.setState({ loadedPublicAppCount: undefined });
      this._lastPublicAppsLimit = undefined;
    }
  }

  onLoadMorePublicApps = () => {
    const appIds = this.collection?.apps;
    if (!appIds?.length) return;
    const { totalApps, initialFetch } = getCollectionAppsPageMeta(
      appIds,
      this.state.loadedPublicAppCount,
    );
    const current = this.state.loadedPublicAppCount != null
      ? this.state.loadedPublicAppCount
      : initialFetch;
    if (current >= totalApps) return;
    this.setState({
      loadedPublicAppCount: nextCollectionAppsLoadedCount(current, initialFetch, totalApps),
    });
  };

  revert = () => {
    const { onClose } = this.props;
    if (this.collection?._id) {
      LogicBaseCollectionCache.revertObject(this.collection);
    }
    onClose();
  };

  editCreate = () => {
    const { onClose } = this.props;
    const { appTopics } = this.state;

    if (!this.collection.title) {
      return this.setState({ error: 'Title Empty' });
    }

    if (this.collection.image && !isUrlValid(this.collection.image)) {
      return this.setState({ error: 'Background Image URL is invalid' });
    }

    if (this.collection.color && isColorInvalid(this.collection.color)) {
      return this.setState({ error: 'Background Color is invalid' });
    }

    const pwd = (this.collection.password && String(this.collection.password).trim()) || '';
    if (pwd) {
      this.collection.socialShare = false;
    }

    if (this.publicApps && this.publicApps.length === this.collection.apps.length) {
      this.collection.apps = this.publicApps.map((a) => a._id);
    }

    if (Object.keys(appTopics).length > 0) {
      this.collection.appTopics = {};
      Object.keys(appTopics).forEach((topicKey) => {
        this.collection.appTopics[topicKey] = appTopics[topicKey].filter((id) =>
          this.collection.apps.includes(id)
        );
      });
    }

    if (this.collection._id) LogicBaseCollectionCache.update(this.collection);
    else LogicBaseCollectionCache.create(this.collection);

    this.setState({ error: '' });
    onClose();
  };

  backgroundColorChanged = (color) => {
    this.collection.color = color;
    this.forceUpdate();
  };

  moveKey = (obj, keyToMove, direction) => {
    const keys = Object.keys(obj);
    const index = keys.indexOf(keyToMove);
    if (index === -1) return;

    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === keys.length - 1) return;

    const swapWith = direction === 'up' ? index - 1 : index + 1;
    [keys[index], keys[swapWith]] = [keys[swapWith], keys[index]];

    const newObj = {};
    keys.forEach((k) => (newObj[k] = obj[k]));
    this.setState({ appTopics: newObj });
  };

  render() {
    const { collection, manageContent } = this.props;
    const { error, appTopics, confirmDeletePopup } = this.state;
    const { account, isLoggedIn } = UserAccount;
    const social = isLoggedIn && LogicBaseUserCache.get(account._id);
    if (!collection) {
      const newCollection = { o: account._id, a: [], fs: true };
      this.collection =
        this.collection || LogicBaseCollectionCache.createObject(newCollection);
    } else {
      this.collection = collection;
    }

    const appIds = this.collection.apps;
    const publicAppsPage = appIds?.length
      ? getCollectionAppsPage(appIds, this.state.loadedPublicAppCount, this._lastPublicAppsLimit)
      : { apps: [], isLoading: false, hasMore: false, lastLoadedLimit: 0, totalApps: 0 };
    if (publicAppsPage.isLoading && !publicAppsPage.apps) {
      this.publicApps = [];
    } else {
      if (!publicAppsPage.isLoading) {
        this._lastPublicAppsLimit = publicAppsPage.lastLoadedLimit;
      }
      this.publicApps = publicAppsPage.apps || [];
    }

    const isOwner = this.collection.owner === account._id;
    const passwordTrim = (this.collection.password && String(this.collection.password).trim()) || '';
    const hasPassword = passwordTrim.length > 0;
    const hasPublicContent = (appIds?.length || 0) > 0;
    const publicOptionDisabled = !social?.logicCircle || !social?.verified || hasPassword || !hasPublicContent;
    const visibilityShowsPublic = !!(this.collection.socialShare && !hasPassword);

    const s3Path = `${account._id}/`;
    const signingUrl = `/v1${AppConstants.S3.signingUrls.userfiles}`;
    return (
      <>
        <Popup showCloseButton allowBackgroundClose onClose={this.revert}>
          <Card style={manageContent ? { overflow: 'hidden' } : undefined}>
            <ModalSheetTitle>{manageContent ? 'Manage Content' : 'Collection Settings'}</ModalSheetTitle>

            <UserProfile>
              <Form style={{ marginTop: '1rem' }}>
                {manageContent ? (
                  <>
                    {isOwner && (
                      <InputBlock>
                        <h3>Filter Tags</h3>

                        {Object.keys(appTopics).map((topic, i) => (
                          <TagRow key={i}>
                            {Object.keys(appTopics).length > 1 ? (
                              <AppSort>
                                {i > 0 && (
                                  <ArrowUpIcon
                                    onClick={() => this.moveKey(appTopics, topic, 'up')}
                                  />
                                )}
                                {i < Object.keys(appTopics).length - 1 && (
                                  <ArrowDownIcon
                                    onClick={() =>
                                      this.moveKey(appTopics, topic, 'down')
                                    }
                                  />
                                )}
                              </AppSort>
                            ) : (
                              <EmptySortSpacer />
                            )}

                            <Input
                              value={topic}
                              placeholder="Filter Tag"
                              maxLength={200}
                              onChange={(e) => {
                                const newTopic = e.target.value;
                                if (appTopics[newTopic]) return;
                                const updated = {};
                                Object.keys(appTopics).forEach((t) => {
                                  updated[t === topic ? newTopic : t] =
                                    appTopics[t];
                                });
                                this.setState({ appTopics: updated });
                              }}
                            />

                            <AppSort>
                              <DeleteIcon
                                onClick={() =>
                                  this.setState({ confirmDeletePopup: topic })
                                }
                              />
                            </AppSort>
                          </TagRow>
                        ))}

                        <SecondaryTextButton
                          type="button"
                          onClick={() => {
                            appTopics[''] = [];
                            this.forceUpdate();
                          }}
                        >
                          + New Tag
                        </SecondaryTextButton>
                      </InputBlock>
                    )}

                    {this.collection._id && hasPublicContent && (
                      <InputBlock style={{ minWidth: 0, maxWidth: '100%' }}>
                        <h3>Content</h3>
                        {publicAppsPage.isLoading && !this.publicApps.length ? (
                          <p>Loading content…</p>
                        ) : (
                          <>
                            <LogicBaseAppList
                              apps={this.publicApps}
                              listView
                              allowSorting
                              captionTitle
                              collection={this.collection}
                              appTopics={appTopics}
                              onTopicSelected={(app, topic) => {
                                const id = app._id;
                                const keys = Object.keys(appTopics);
                                const current = keys.find((k) => appTopics[k].includes(id));
                                if (current === topic) {
                                  const arr = appTopics[topic];
                                  const idx = arr.indexOf(id);
                                  if (idx >= 0) arr.splice(idx, 1);
                                } else {
                                  keys.forEach((k) => {
                                    const arr = appTopics[k];
                                    const idx = arr.indexOf(id);
                                    if (idx >= 0) arr.splice(idx, 1);
                                  });
                                  if (topic && appTopics[topic] && appTopics[topic].indexOf(id) < 0) {
                                    appTopics[topic].push(id);
                                  }
                                }
                                this.forceUpdate();
                              }}
                            />
                            {publicAppsPage.hasMore && (
                              <SecondaryTextButton
                                type="button"
                                onClick={this.onLoadMorePublicApps}
                                style={{ marginTop: '0.75rem' }}
                              >
                                Load more content
                              </SecondaryTextButton>
                            )}
                          </>
                        )}
                      </InputBlock>
                    )}
                  </>
                ) : (
                  <>
                    {isOwner && (
                      <InputBlock>
                        <Label>Visibility</Label>
                        <SelectField
                          value={visibilityShowsPublic ? 'public' : 'private'}
                          onChange={(e) => {
                            const nextPublic = e.target.value === 'public';
                            if (nextPublic && publicOptionDisabled) return;
                            this.collection.socialShare = nextPublic;
                            this.forceUpdate();
                          }}
                        >
                          <option value="public" disabled={publicOptionDisabled}>
                            Publicly searchable
                          </option>
                          <option value="private">Only on your profile</option>
                        </SelectField>
                      </InputBlock>
                    )}

                    <InputBlock>
                      <Label>Title</Label>
                      <Input
                        value={this.collection.title || ''}
                        placeholder="Title"
                        maxLength={150}
                        onChange={(e) => {
                          this.collection.title = e.target.value;
                          this.forceUpdate();
                        }}
                      />
                    </InputBlock>

                    <InputBlock>
                      <Label>Description</Label>
                      <TextArea
                        value={(this.collection.description || '').replace(/\\n/g, '\n')}
                        placeholder="Short Description"
                        maxLength={1000}
                        onChange={(e) => {
                          this.collection.description = e.target.value;
                          this.forceUpdate();
                        }}
                      />
                    </InputBlock>

                    {isOwner && !visibilityShowsPublic && (
                      <InputBlock>
                        <Label>Password</Label>
                        <Input
                          value={this.collection.password || ''}
                          placeholder="Password"
                          maxLength={256}
                          onChange={(e) => {
                            this.collection.password = e.target.value;
                            this.forceUpdate();
                          }}
                        />
                      </InputBlock>
                    )}

                    <InputBlock>
                      <Label>Color</Label>
                      <InlineFieldRow>
                        <Input
                          value={this.collection.color || ''}
                          placeholder="Color value"
                          onChange={(e) =>
                            this.backgroundColorChanged(e.target.value)
                          }
                        />
                        <ColorPicker
                          color={
                            this.collection.color
                              ? convertColorToHex(this.collection.color)
                              : Constants.PrimaryColor
                          }
                          onChange={(color) => {
                            this.collection.color = color;
                            this.forceUpdate();
                          }}
                        />
                      </InlineFieldRow>
                    </InputBlock>

                    <ContentSourceField
                      label="Image"
                      placeholder="16:9 image URL"
                      maxLength={1000}
                      value={this.collection.image || ''}
                      onChange={(v) => {
                        this.collection.image = v;
                        this.forceUpdate();
                      }}
                      validateUrlOnBlur={false}
                      s3Path={s3Path}
                      signingUrl={signingUrl}
                      hideUploadText
                      uploadPickerMode="imageFile"
                      onUploadFinish={(fullUrl) => {
                        const url = fullUrl.signedUrl.split('?')[0];
                        this.collection.image = url;
                        this.forceUpdate();
                      }}
                    />

                    {isOwner && (
                      <InputBlock>
                        <CheckboxRow>
                          <input
                            type="checkbox"
                            disabled={!!this.collection.premiumCollection}
                            checked={!!this.collection.forceSubscribe || !!this.collection.premiumCollection}
                            onChange={() => {
                              this.collection.forceSubscribe = !this.collection.forceSubscribe;
                              this.forceUpdate();
                            }}
                          />
                          Force users to subscribe to view?
                        </CheckboxRow>
                        <Tooltip text="<b>Only Verified Creators</b> can access this setting. This enables Logictry to share the emails collected with you.">
                          <CheckboxRow style={{ opacity: !(social?.logicCircle && social?.verified) ? 0.4 : 1 }}>
                            <input
                              type="checkbox"
                              disabled={!(social?.logicCircle && social?.verified)}
                              checked={!!this.collection.collectEmails}
                              onChange={() => {
                                this.collection.collectEmails = !this.collection.collectEmails;
                                this.forceUpdate();
                              }}
                            />
                            Ask users to share their email?
                          </CheckboxRow>
                        </Tooltip>
                      </InputBlock>
                    )}
                  </>
                )}
              </Form>

              {error && <WarningText>{error}</WarningText>}
            </UserProfile>

            <DialogActions>
              <CancelButton onClick={this.revert}>Cancel</CancelButton>
              <ConfirmButton onClick={this.editCreate}>
                {collection ? 'Update' : 'Create'}
              </ConfirmButton>
            </DialogActions>
          </Card>
        </Popup>

        {confirmDeletePopup && (
          <ConfirmDialog
            open
            text="Confirm Remove"
            description="Are you sure you wish to remove?"
            onYes={() => {
              delete appTopics[confirmDeletePopup];
              this.setState({ confirmDeletePopup: null });
            }}
            onNo={() => this.setState({ confirmDeletePopup: null })}
          />
        )}
      </>
    );
  }
}
